import { useEffect, useState } from 'react';
import WithLoadingSpinner from 'components/withLoadingSpinner/WithLoadingSpinner';
import { GenericTable } from 'components/table/IdmTable';
import { apiRoutes } from 'core/apis/itineraryViewer';
import { useActivityExceptionsLazyQuery } from 'core/graphql/Activity/ActivityExceptions.generated';
import { useDeliveriesStatisticsLazyQuery } from 'core/graphql/Activity/DeliveriesStatistics.generated';
import useDebounce from 'core/hooks/useDebounce';
import { parseISO } from 'date-fns';
import { formatDate } from 'core/utils/DateUtils';
import useAppDispatch from 'core/hooks/useAppDispatch';
import {
  setCourier,
  setDate,
  setItineraryVersionId,
} from 'pages/itinerary/ItinerarySlice';
import { useNavigate } from 'react-router-dom';
import useUrl from 'core/hooks/useUrl';
import DesignButton from 'components/button/DesignButton';
import ActivityFilter from '../../components/activityFilter/ActivityFilter';
import { ActivityFileExport } from '../../components/activityFileExport/ActivityFileExport';
import deliveriesStatsmodel, {
  DeliveriesStatsElement,
} from './DeliveriesStatisticsTableConfiguration';
import itineraryRecomputationModel, {
  ItineraryRecomputationsElement,
} from './ItineraryStatisticsTableConfiguration';
import { withAuthenticationRequired } from 'react-oidc-context';

const Activity = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { paramValue: dateRange } = useUrl<string[]>('DateRange');
  const { paramValue: selectedCouriers } = useUrl<number[]>('CourierId');
  const debouncedCouriers = useDebounce<number[] | null>(
    selectedCouriers,
    1000,
  );
  const [itinerariesPageNumber, setItinerariesPageNumber] = useState(1);
  const [deliveriesPageNumber, setDeliveriesPageNumber] = useState(1);

  const [
    getItinerariesRecomputations,
    {
      data: itinerariesRecomputations,
      loading: loadingItinerariesStatistics,
      refetch: refetchItinerariesStatistics,
    },
  ] = useActivityExceptionsLazyQuery();

  const [
    getDeliveriesStatistics,
    {
      data: deliveriesStatistics,
      loading: loadingDeliveriesStatistics,
      refetch: refetchDeliveriesStatistics,
    },
  ] = useDeliveriesStatisticsLazyQuery();

  useEffect(() => {
    if (debouncedCouriers && debouncedCouriers?.length > 0 && dateRange) {
      setItinerariesPageNumber(1);
      getItinerariesRecomputations({
        variables: {
          startDate: dateRange[0],
          endDate: dateRange[1],
          courierIds: debouncedCouriers,
          skip: 0,
          take: 10,
        },
      });
    }
  }, [debouncedCouriers, dateRange, getItinerariesRecomputations]);

  useEffect(() => {
    if (debouncedCouriers && debouncedCouriers?.length > 0 && dateRange) {
      setDeliveriesPageNumber(1);
      getDeliveriesStatistics({
        variables: {
          startDate: dateRange[0],
          endDate: dateRange[1],
          courierIds: debouncedCouriers,
          skip: 0,
          take: 10,
        },
      });
    }
  }, [debouncedCouriers, dateRange, getDeliveriesStatistics]);

  const refetchData = () => {
    if (dateRange && debouncedCouriers) {
      refetchItinerariesStatistics({
        startDate: dateRange[0],
        endDate: dateRange[1],
        courierIds: debouncedCouriers,
        skip: (itinerariesPageNumber - 1) * 10,
        take: 10,
      });
      refetchDeliveriesStatistics({
        startDate: dateRange[0],
        endDate: dateRange[1],
        courierIds: debouncedCouriers,
        skip: (deliveriesPageNumber - 1) * 10,
        take: 10,
      });
    }
  };

  const itineraryStatsPageChanged = (pageNumber: number) => {
    setItinerariesPageNumber(pageNumber);
    if (dateRange && debouncedCouriers) {
      getItinerariesRecomputations({
        variables: {
          startDate: dateRange[0],
          endDate: dateRange[1],
          courierIds: debouncedCouriers,
          skip: (pageNumber - 1) * 10,
          take: 10,
        },
      });
    }
  };

  const deliveryStatsPageChanged = (pageNumber: number) => {
    setDeliveriesPageNumber(pageNumber);
    if (dateRange && debouncedCouriers) {
      getDeliveriesStatistics({
        variables: {
          startDate: dateRange[0],
          endDate: dateRange[1],
          courierIds: debouncedCouriers,
          skip: (pageNumber - 1) * 10,
          take: 10,
        },
      });
    }
  };

  let itineraryRecomputationsData: ItineraryRecomputationsElement[] = [];
  const anyItineraryStatistics =
    itinerariesRecomputations &&
    itinerariesRecomputations.ActivityExceptions &&
    itinerariesRecomputations.ActivityExceptions?.items &&
    itinerariesRecomputations.ActivityExceptions?.items.length > 0;

  if (anyItineraryStatistics) {
    itineraryRecomputationsData =
      itinerariesRecomputations.ActivityExceptions!.items!.map((x) => ({
        id: x.courierId,
        date: x.date,
        itineraryRecomputationsCount: x.noOfCustomItineraryComputations,
        onClickHandler: () => {
          dispatch(setDate(formatDate(parseISO(x?.date))));
          dispatch(setCourier(x.courierId));
          dispatch(setItineraryVersionId(null));
          return navigate('/');
        },
      })) as ItineraryRecomputationsElement[];
  }

  let deliveryStatisticsData: DeliveriesStatsElement[] = [];
  const anyDeliveriesStatistics =
    deliveriesStatistics &&
    deliveriesStatistics.DeliveriesStatistics &&
    deliveriesStatistics.DeliveriesStatistics?.items &&
    deliveriesStatistics.DeliveriesStatistics?.items.length > 0;

  if (anyDeliveriesStatistics) {
    deliveryStatisticsData =
      deliveriesStatistics!.DeliveriesStatistics!.items!.map((x) => ({
        courierId: x?.courierId,
        date: x?.date,
        pastETADeliveriesCount: x?.noOfDeliveriesPastEta,
        outOfOrderDeliveriesCount: x?.noOfDeliveriesOutOfOrder,
        onClickHandler: () => {
          dispatch(setDate(formatDate(parseISO(x?.date))));
          dispatch(setCourier(x?.courierId!));
          dispatch(setItineraryVersionId(null));
          return navigate('/');
        },
        delivered: x?.dailyProgress?.finishedDeliveries,
        totalDeliveries: x?.dailyProgress?.totalDeliveries,
      })) as DeliveriesStatsElement[];
  }

  let itineraryStatsPageCount = 1;
  if (anyItineraryStatistics) {
    itineraryStatsPageCount = Math.ceil(
      itinerariesRecomputations.ActivityExceptions!.totalCount / 10,
    );
  }

  let deliveriesStatsPageCount = 1;
  if (anyDeliveriesStatistics) {
    deliveriesStatsPageCount = Math.ceil(
      deliveriesStatistics.DeliveriesStatistics!.totalCount / 10,
    );
  }

  return (
    <WithLoadingSpinner
      isLoading={loadingDeliveriesStatistics || loadingItinerariesStatistics}
    >
      <div className="flex h-full py-2 dark:bg-design-black dark:text-white">
        <div className="mx-8 flex w-1/4 flex-col pr-2">
          <ActivityFilter />
          {dateRange && debouncedCouriers && debouncedCouriers.length > 0 && (
            <div className="mt-4">
              <DesignButton onClickHandler={refetchData}>Refresh</DesignButton>
            </div>
          )}
        </div>
        <div className="max-h-screen w-3/4 overflow-y-auto">
          <div className="my-5 text-left text-xl font-bold text-black dark:text-white">
            Itinerary recomputations
          </div>
          <GenericTable
            model={itineraryRecomputationModel}
            elements={itineraryRecomputationsData}
            pageNumber={itinerariesPageNumber}
            totalElements={itineraryStatsPageCount}
            pageChanged={itineraryStatsPageChanged}
          />
          {dateRange &&
            debouncedCouriers &&
            debouncedCouriers.length &&
            anyItineraryStatistics && (
              <ActivityFileExport
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                courierIds={debouncedCouriers}
                exportName="Export Itinerary Statistics"
                endpoint={apiRoutes.EXPORT_RECOMPUTATIONS_STATISTICS.url}
                fileName={`Itinerary Recomputations ${dateRange[0]} ${dateRange[1]}.xlsx`}
              />
            )}
          <div className="my-5 text-left text-xl font-bold text-black dark:text-white">
            Deliveries statistics
          </div>
          <GenericTable
            model={deliveriesStatsmodel}
            elements={deliveryStatisticsData}
            pageNumber={deliveriesPageNumber}
            totalElements={deliveriesStatsPageCount}
            pageChanged={deliveryStatsPageChanged}
          />
          {dateRange &&
            debouncedCouriers &&
            debouncedCouriers.length > 0 &&
            anyDeliveriesStatistics && (
              <ActivityFileExport
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                courierIds={debouncedCouriers}
                exportName="Export Delivery Statistics"
                endpoint={apiRoutes.EXPORT_DELIVERIES_STATISTICS.url}
                fileName={`Deliveries Statistics ${dateRange[0]} ${dateRange[1]}.xlsx`}
              />
            )}
        </div>
      </div>
    </WithLoadingSpinner>
  );
};

export default withAuthenticationRequired(Activity);
